import { RecentSearchListItem } from '@/src/hooks/srp/recentSearch'
import { TranslationFunction } from '@/src/types/translations'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import { Box, Tab, Tabs, alpha } from '@mui/material'
import React from 'react'
import { AISearchBox } from '../../AISearch/homeAISearchBox'
import SearchForm from '../searchForm'
import { TabPanel, a11yProps } from '../tabPanel'

interface FlightSearchTabsProps {
    t: TranslationFunction<'header'>
    tabValue: number
    onTabChange: (event: React.SyntheticEvent, newValue: number) => void
    isMobile: boolean
    serverRecentSearches: RecentSearchListItem[]
    hideMulticity?: boolean
    fromParam?: any[] | null
    toParam?: any[] | null
    showVisaTab?: boolean
    showFlightsHotelsTab?: boolean
    showAiSearchTab?: boolean
}

export const FlightSearchTabs: React.FC<FlightSearchTabsProps> = ({
    t,
    tabValue,
    onTabChange,
    isMobile,
    serverRecentSearches,
    hideMulticity,
    fromParam,
    toParam,
    showVisaTab = false,
    showFlightsHotelsTab = false,
    showAiSearchTab = false
}) => {
    const showThirdTab = showVisaTab || showFlightsHotelsTab
    const aiTabIndex = 0
    const flightsTabIndex = showAiSearchTab ? 1 : 0
    const hotelsTabIndex = flightsTabIndex + 1
    const thirdTabIndex = hotelsTabIndex + 1

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
            {/* Tabs Container - Separate white box */}
            <Box
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'common.white',
                    borderRadius: 5,
                    border: 1,
                    borderColor: 'grey.300',
                    py: 1.5,
                    px: 2.5,
                    width: 'fit-content',
                    position: 'relative',
                    zIndex: 2
                }}
            >
                <Tabs
                    value={tabValue}
                    onChange={onTabChange}
                    aria-label="flight search tabs"
                    sx={{
                        minHeight: 'unset',
                        '& .MuiTabs-flexContainer': {
                            gap: 0
                        },
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: 'grey.600',
                            px: 3,
                            py: 1,
                            minWidth: 'unset',
                            minHeight: 'unset',
                            borderRadius: 0.5,
                            position: 'relative',
                            transition: 'all 0.2s ease',
                            '&.Mui-selected': {
                                fontWeight: 600,
                                color: 'primary.main',
                                bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                border: theme => `1.5px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                borderRadius: 5,
                                zIndex: 1,
                                mx: 0.5

                            },
                            '&:not(:first-of-type)': {
                                borderLeft: '1px solid',
                                borderColor: 'grey.300'
                            }
                        },
                        '& .MuiTabs-indicator': {
                            display: 'none'
                        }
                    }}
                >
                    {showAiSearchTab && (
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />
                                    <Box component="span">{t('flights')}</Box>
                                </Box>
                            }
                            {...a11yProps(aiTabIndex)}
                        />
                    )}
                    <Tab label={t('flights')} {...a11yProps(flightsTabIndex)} />
                    <Tab label={t('hotels')} {...a11yProps(hotelsTabIndex)} />
                    {showThirdTab && (
                        <Tab
                            label={showVisaTab ? t('visa') : `${t('flights')} + ${t('hotels')}`}
                            {...a11yProps(thirdTabIndex)}
                        />
                    )}
                </Tabs>
            </Box>

            {/* Search Form Container - Separate white box with top overlapped by tabs */}
            <Box
                sx={{
                    bgcolor: 'common.white',
                    px: 3,
                    pt: 2,
                    pb: 3,
                    borderRadius: 2,
                    border: 2,
                    borderColor: 'grey.100',
                    width: '100%',
                    position: 'relative',
                    zIndex: 1,
                    mt: '-12px' // Negative margin to create 20% overlap with tabs bottom (approximately 20% of tabs container height)
                }}
            >
                {showAiSearchTab && (
                    <TabPanel value={tabValue} index={aiTabIndex}>
                        <AISearchBox />
                    </TabPanel>
                )}

                <TabPanel value={tabValue} index={flightsTabIndex}>
                    <SearchForm
                        isMobile={isMobile}
                        serverRecentSearches={serverRecentSearches}
                        hideMulticity={hideMulticity}
                        fromParam={fromParam}
                        toParam={toParam}
                    />
                </TabPanel>
            </Box>
        </Box>
    )
}
